import CheckCircle from "@mui/icons-material/CheckCircle";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import { useEnabledChannels } from "../../modules/channels/hooks/use-enabled-channels";
import { WhatsAppTemplatePreview } from "../../modules/whatsapp-moment/components/whatsapp-template-preview";
import { useWhatsAppTemplates } from "../../modules/whatsapp-moment/hooks/use-whatsapp-templates";
import { WhatsAppIcon } from "../../styles/icons/whatsapp";

import type { FC } from "react";

const ONBOARDING_EMAIL = "onboarding@changeengine.com";
const SETUP_REQUEST_HREF = `mailto:${ONBOARDING_EMAIL}?subject=WhatsApp%20enablement%20request`;

export const WhatsApp: FC = () => {
  const enabledChannels = useEnabledChannels();
  const enabled = enabledChannels.data?.includes("whatsapp") ?? false;

  useEffect(() => {
    if (!enabledChannels.loading && !enabledChannels.error) {
      // oxlint-disable-next-line unicorn/prefer-global-this
      window.pendo?.track("whatsapp-setup-flow-viewed", { enabled });
    }
  }, [enabled, enabledChannels.error, enabledChannels.loading]);

  if (enabledChannels.loading) return <LinearProgress />;

  if (enabledChannels.error) {
    return <Alert severity="error">Failed to load WhatsApp configuration</Alert>;
  }

  if (!enabled) return <WhatsAppSetupRequest />;

  return <EnabledWhatsApp />;
};

const WhatsAppSetupRequest: FC = () => (
  <Box
    sx={{
      alignItems: "center",
      border: 1,
      borderColor: "divider",
      display: "flex",
      flexDirection: "column",
      minHeight: 164,
      justifyContent: "center",
      px: 3,
      py: 4,
      textAlign: "center",
    }}
  >
    <WhatsAppIcon sx={{ fontSize: 48, mb: 1 }} />
    <Typography component="h2" variant="h6" sx={{ mb: 1 }}>
      Hey, WhatsApp!
    </Typography>
    <Typography variant="body2">
      Reach employees where they already are. Contact{" "}
      <Link
        href={SETUP_REQUEST_HREF}
        // oxlint-disable-next-line unicorn/prefer-global-this
        onClick={() => window.pendo?.track("whatsapp-setup-request-submitted")}
      >
        {ONBOARDING_EMAIL}
      </Link>{" "}
      to learn how to add WhatsApp messaging to your ChangeEngine account.
    </Typography>
  </Box>
);

const EnabledWhatsApp: FC = () => {
  const templates = useWhatsAppTemplates();

  if (templates.loading) return <LinearProgress />;

  if (templates.error) {
    return <Alert severity="error">Failed to load WhatsApp configuration</Alert>;
  }

  return (
    <Stack spacing={4}>
      <Card variant="outlined" sx={{ width: 260 }}>
        <CardContent>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <WhatsAppIcon />
            <Typography variant="body1">WhatsApp</Typography>
          </Stack>
          <Chip color="success" icon={<CheckCircle />} label="Active" size="small" sx={{ mt: 2 }} />
        </CardContent>
      </Card>

      <Box>
        <Typography component="h2" variant="h6" sx={{ mb: 0.5 }}>
          We’re here to help.
        </Typography>
        <Typography variant="body2">
          Contact <Link href={`mailto:${ONBOARDING_EMAIL}`}>{ONBOARDING_EMAIL}</Link> to request
          template updates, ask questions, or get support from our Implementation team.
        </Typography>
      </Box>

      <Box>
        <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
          Your templates
        </Typography>
        {(templates.data?.length ?? 0) > 0 ? (
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            }}
          >
            {templates.data?.map((template) => (
              <WhatsAppTemplatePreview
                key={template.id}
                template={template}
                heading={template.name}
                disabled
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No WhatsApp templates have been assigned yet.
          </Typography>
        )}
      </Box>
    </Stack>
  );
};
